import { createApi } from "@reduxjs/toolkit/query/react";

import {
  ContractInvoice,
  TContractInvoiceSingle,
  TContracts,
  TDocketInvoice,
  TDocketInvoiceItem,
} from "@/interfaces/invoicing.interface";
import { IProject } from "@/interfaces/project.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const invoicingApi = createApi({
  reducerPath: "invoicingApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["docketInvoicing", "contractInvoicing", "invoicedContract"],
  endpoints: (builder) => ({
    getDocketInvoicing: builder.query<
      GeneralResponse<TDocketInvoice>,
      {
        filter?: string;
      }
    >({
      query: ({ filter }) => ({
        url: `/workspace/${getWorkspace()}/invoice/docket?${filter ?? ""}`,
      }),
      providesTags: ["docketInvoicing"],
    }),

    getSingleDocketInvoice: builder.query<
      TDocketInvoiceItem,
      {
        id: string;
      }
    >({
      query: ({ id }) => ({
        url: `/workspace/${getWorkspace()}/invoice/docket/${id}`,
      }),
      providesTags: ["docketInvoicing"],
      transformResponse: (response: APIResponse<TDocketInvoiceItem>) =>
        response?.data,
    }),
    muateInvoice: builder.mutation<
      object,
      { id?: string; data?: object; method: "POST" | "PUT" | "DELETE" }
    >({
      query: ({ data, id, method }) => ({
        url: `/workspace/${getWorkspace()}/invoice/docket/${id ? id : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["docketInvoicing"],
    }),

    //***FOR CONTRACT INVOICING***//

    //Get all ready to invoice contract invoices
    getAllReadyInvoiceContracts: builder.query<
      GeneralResponse<ContractInvoice>,
      {
        filter?: string;
      }
    >({
      query: ({ filter }) => ({
        url: `/workspace/${getWorkspace()}/invoice/contract/all?${filter ?? ""}`,
      }),
      providesTags: ["contractInvoicing"],
    }),
    //Get all invoiced contract invoices
    getInvoicedContracts: builder.query<
      GeneralResponse<ContractInvoice>,
      { filter: string }
    >({
      query: ({ filter }) => ({
        url: `/workspace/${getWorkspace()}/invoice/contract?${filter ?? ""}`,
      }),
      providesTags: ["invoicedContract"],
    }),
    //Get multiple contract invoices
    getMultiContract: builder.query<
      { contracts: TContracts; projects: IProject[] },
      { contractIds: string[] }
    >({
      query: ({ contractIds }) => ({
        url: `/workspace/${getWorkspace()}/invoice/contract/multiple?claimIds=${encodeURIComponent(
          JSON.stringify(contractIds),
        )}
        `,
      }),
      transformResponse: (
        response: APIResponse<{
          contracts: TContracts;
          projects: IProject[];
        }>,
      ) => response?.data,
    }),

    getSingleContractInvoice: builder.query<
      TContractInvoiceSingle,
      { contractInvoiceId: string }
    >({
      query: ({ contractInvoiceId }) => ({
        url: `/workspace/${getWorkspace()}/invoice/contract/${contractInvoiceId}`,
      }),
      transformResponse: (response: APIResponse<TContractInvoiceSingle>) =>
        response?.data,
      providesTags: ["contractInvoicing"],
    }),
    muateContractInvoice: builder.mutation<
      object,
      { id?: string; data?: object; method: "POST" | "PUT" | "DELETE" }
    >({
      query: ({ data, id, method }) => ({
        url: `/workspace/${getWorkspace()}/invoice/contract/${id ? id : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["contractInvoicing", "invoicedContract"],
    }),
  }),
});

export const {
  useGetDocketInvoicingQuery,
  useGetSingleDocketInvoiceQuery,
  useMuateInvoiceMutation,
  useGetAllReadyInvoiceContractsQuery,
  useGetInvoicedContractsQuery,
  useGetMultiContractQuery,
  useGetSingleContractInvoiceQuery,
  useMuateContractInvoiceMutation,
} = invoicingApi;
